import { useState, useEffect, useCallback, useRef } from 'react';
import './ConversationAnalytics.css';

interface ConversationAnalyticsProps {
  apiBaseUrl: string;
}

interface CategoryDef {
  label: string;
  pct: number;
}

interface ObjectionDef {
  text: string;
  pct: string;
}

const COUNTER_TARGET = 1247;

const CATEGORIES: CategoryDef[] = [
  { label: 'Sizing & fit', pct: 38 },
  { label: 'Style advice', pct: 24 },
  { label: 'Returns', pct: 16 },
];

const OBJECTIONS: ObjectionDef[] = [
  { text: 'too pricey', pct: '12%' },
  { text: 'wrong size', pct: '8%' },
  { text: 'shipping concerns', pct: '5%' },
];

const INSIGHT = 'Shoppers asking about layering converted 2.3x higher this week.';

function SparkleIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z" />
    </svg>
  );
}

export function ConversationAnalytics(_props: ConversationAnalyticsProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const rafRef = useRef(0);

  const [headerVisible, setHeaderVisible] = useState(false);
  const [counterVisible, setCounterVisible] = useState(false);
  const [counterValue, setCounterValue] = useState(0);
  const [categoriesLabelVisible, setCategoriesLabelVisible] = useState(false);
  const [barsActive, setBarsActive] = useState<boolean[]>(() => CATEGORIES.map(() => false));
  const [objectionsLabelVisible, setObjectionsLabelVisible] = useState(false);
  const [objectionsVisible, setObjectionsVisible] = useState<boolean[]>(() => OBJECTIONS.map(() => false));
  const [insightVisible, setInsightVisible] = useState(false);
  const [insightChars, setInsightChars] = useState(0);
  const [fading, setFading] = useState(false);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const animateCounter = useCallback(
    (target: number, duration: number) =>
      new Promise<void>((resolve) => {
        const start = performance.now();
        const tick = (now: number) => {
          if (!loopRef.current) {
            resolve();
            return;
          }
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          setCounterValue(Math.round(target * eased));
          if (t < 1) {
            rafRef.current = requestAnimationFrame(tick);
          } else {
            resolve();
          }
        };
        rafRef.current = requestAnimationFrame(tick);
      }),
    [],
  );

  const typeInsight = useCallback(async () => {
    for (let i = 1; i <= INSIGHT.length; i++) {
      if (!loopRef.current) return;
      setInsightChars(i);
      await sleep(30);
    }
  }, [sleep]);

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setHeaderVisible(false);
      setCounterVisible(false);
      setCounterValue(0);
      setCategoriesLabelVisible(false);
      setBarsActive(CATEGORIES.map(() => false));
      setObjectionsLabelVisible(false);
      setObjectionsVisible(OBJECTIONS.map(() => false));
      setInsightVisible(false);
      setInsightChars(0);
      setFading(false);
      await sleep(600);

      // Beat 1: header
      if (!loopRef.current) return;
      setHeaderVisible(true);
      await sleep(500);

      // Beat 2: big counter ticks up
      if (!loopRef.current) return;
      setCounterVisible(true);
      await sleep(250);
      if (!loopRef.current) return;
      await animateCounter(COUNTER_TARGET, 1600);
      await sleep(900);

      // Beat 3: categories label + animated bars
      if (!loopRef.current) return;
      setCategoriesLabelVisible(true);
      await sleep(450);
      for (let i = 0; i < CATEGORIES.length; i++) {
        if (!loopRef.current) return;
        setBarsActive((prev) => prev.map((v, j) => (j <= i ? true : v)));
        await sleep(320);
      }
      // wait for the last bar's fill transition (~700ms) to complete
      await sleep(700);
      await sleep(500);

      // Beat 4: objections label + pills
      if (!loopRef.current) return;
      setObjectionsLabelVisible(true);
      await sleep(450);
      for (let i = 0; i < OBJECTIONS.length; i++) {
        if (!loopRef.current) return;
        setObjectionsVisible((prev) => prev.map((v, j) => (j <= i ? true : v)));
        await sleep(320);
      }
      await sleep(500);

      // Beat 5: typewriter insight card
      if (!loopRef.current) return;
      setInsightVisible(true);
      await sleep(280);
      await typeInsight();

      // Beat 6: hold the final state
      await sleep(2000);

      // Beat 7: fade out, brief pause, restart
      if (!loopRef.current) return;
      setFading(true);
      await sleep(700);
      await sleep(500);
    }
  }, [sleep, animateCounter, typeInsight]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      cancelAnimationFrame(rafRef.current);
    };
  }, [runLoop]);

  const formattedCounter = counterValue.toLocaleString('en-US');

  return (
    <div className={`conversation-analytics ${fading ? 'ca-fading' : ''}`}>
      {/* Header */}
      <div className={`ca-header ${headerVisible ? 'ca-visible' : ''}`}>
        <div className="sc-section-label">This week's conversations</div>
      </div>

      {/* Counter */}
      <div className={`ca-counter sc-glass-card ${counterVisible ? 'ca-visible' : ''}`}>
        <div className="ca-counter-value">{formattedCounter}</div>
        <div className="ca-counter-caption">conversations analyzed</div>
      </div>

      {/* Top question categories */}
      <div className="ca-section">
        <div
          className={`sc-section-label ca-reveal ${categoriesLabelVisible ? 'ca-visible' : ''}`}
        >
          Top question categories
        </div>
        <div className="ca-bars">
          {CATEGORIES.map((cat, i) => (
            <div
              key={cat.label}
              className={`ca-bar-row ${barsActive[i] ? 'ca-bar-active' : ''}`}
            >
              <div className="ca-bar-label">{cat.label}</div>
              <div className="ca-bar-track sc-glass-subtle">
                <div
                  className="ca-bar-fill"
                  style={{ width: barsActive[i] ? `${cat.pct}%` : '0%' }}
                />
              </div>
              <div className="ca-bar-pct">{cat.pct}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Common objections */}
      <div className="ca-section">
        <div
          className={`sc-section-label ca-reveal ${objectionsLabelVisible ? 'ca-visible' : ''}`}
        >
          Common objections
        </div>
        <div className="ca-objections">
          {OBJECTIONS.map((obj, i) => (
            <div
              key={obj.text}
              className={`sc-pill ca-objection ${objectionsVisible[i] ? 'ca-visible' : ''}`}
            >
              <span className="ca-objection-text">{obj.text}</span>
              <span className="ca-objection-sep">,</span>
              <span className="ca-objection-pct">{obj.pct}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Insight card with typewriter */}
      <div className={`ca-insight sc-glass-card ${insightVisible ? 'ca-visible' : ''}`}>
        <div className="ca-insight-icon">
          <SparkleIcon />
        </div>
        <div className="ca-insight-body">
          <span className="ca-insight-text">{INSIGHT.slice(0, insightChars)}</span>
          {insightVisible && insightChars < INSIGHT.length && <span className="ca-cursor" />}
        </div>
      </div>
    </div>
  );
}
